import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { validateCart } from "@/lib/api";

const CartContext = createContext(null);
const STORAGE_KEY = "vault_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [validated, setValidated] = useState(null);
  const [loadingValidation, setLoadingValidation] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const refreshValidation = useCallback(async (list) => {
    const source = list || items;
    if (source.length === 0) {
      setValidated({ items: [], subtotal_eur: 0, shipping_eur: 0, total_eur: 0, free_shipping_threshold_eur: 75 });
      return;
    }
    setLoadingValidation(true);
    try {
      const res = await validateCart(source.map((i) => ({ product_id: i.product_id, quantity: i.quantity })));
      setValidated(res);
    } catch (e) {
      setValidated(null);
    } finally {
      setLoadingValidation(false);
    }
  }, [items]);

  useEffect(() => {
    if (open) refreshValidation();
  }, [open, refreshValidation]);

  const addItem = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      let next;
      if (existing) {
        next = prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + quantity } : i
        );
      } else {
        next = [
          ...prev,
          {
            product_id: product.id,
            slug: product.slug,
            name: product.name,
            price: product.price_eur,
            image: product.images?.[0] || "",
            expansion_name: product.expansion_name,
            quantity,
          },
        ];
      }
      return next;
    });
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => (i.product_id === productId ? { ...i, quantity } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        count,
        subtotal,
        open,
        setOpen,
        addItem,
        updateQuantity,
        removeItem,
        clear,
        validated,
        loadingValidation,
        refreshValidation,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
